/**
 * @fileoverview Protected route component.
 * @summary Redirects to login if user is not authenticated.
 * @package PlayItNow
 * @module apps/front/components/ProtectedRoute
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { SpinnerBig } from './SpinnerBig'
import { ADMIN_DASHBOARD_URL } from '@/config'

/**
 * @summary Protected route props.
 */
interface ProtectedRouteProps {
  children: ReactNode
  requireAdmin?: boolean
}

/**
 * @summary Protected route component.
 * @param children Child components to render if authenticated.
 * @param requireAdmin If true, requires admin role (band or superadmin).
 * Superadmins are redirected to the separate SuperAdmin interface.
 */
export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()

  // Redirect superadmin to the SuperAdmin interface (front-admin app)
  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.role === 'superadmin' && requireAdmin) {
      window.location.href = ADMIN_DASHBOARD_URL
    }
  }, [isLoading, isAuthenticated, user, requireAdmin])

  if (isLoading) {
    return <SpinnerBig />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (requireAdmin && user && user.role !== 'band' && user.role !== 'superadmin') {
    return <Navigate to="/login" replace />
  }

  // Show spinner while redirecting superadmin
  if (requireAdmin && user?.role === 'superadmin') {
    return <SpinnerBig />
  }

  return <>{children}</>
}
